import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getChatHistory, streamChat } from "../lib/api";
import { Bot, User, Send, Sparkles, ChevronRight } from "lucide-react";

const SESSION_KEY = "voip_ai_session";

function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const SUGGESTIONS = [
  "How do I verify QoS trust on a Catalyst access port?",
  "SIP trunk registers but calls fail with 503 — where do I start?",
  "Steps to check a duplex mismatch causing choppy audio",
  "How do I read RTMT to confirm a phone is registered?",
];

// Minimal renderer: splits ``` fenced code blocks from prose.
function renderContent(text) {
  const parts = text.split(/```/g);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const body = part.replace(/^[a-zA-Z0-9]*\n/, "");
      return (
        <pre key={i} className="my-2 rounded-md border border-zinc-800 bg-black p-3 overflow-x-auto">
          <code className="font-mono text-[13px] text-emerald-300 whitespace-pre-wrap">{body}</code>
        </pre>
      );
    }
    return (
      <span key={i} className="whitespace-pre-wrap">
        {part.split(/(`[^`]+`)/g).map((seg, j) =>
          seg.startsWith("`") && seg.endsWith("`") ? (
            <code key={j} className="font-mono text-[12px] bg-zinc-800 text-blue-300 px-1.5 py-0.5 rounded">{seg.slice(1, -1)}</code>
          ) : (
            seg
          )
        )}
      </span>
    );
  });
}

export default function Assistant() {
  const [params] = useSearchParams();
  const context = params.get("context") || "";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const autoSent = useRef(false);

  useEffect(() => {
    getChatHistory(sessionId.current).then(setMessages).catch(() => {});
  }, []);

  // Auto-send a prefilled question when arriving from a wizard step (AI Deep-Dive).
  useEffect(() => {
    const ask = params.get("ask");
    if (ask && !autoSent.current) {
      autoSent.current = true;
      send(ask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);

    // Insert an empty assistant bubble we fill as tokens stream in
    let assistantIndex = -1;
    setMessages((m) => {
      assistantIndex = m.length;
      return [...m, { role: "assistant", content: "", streaming: true }];
    });

    await streamChat(
      { session_id: sessionId.current, message: msg, context: context || null },
      {
        onDelta: (delta) => {
          setMessages((m) => {
            const copy = [...m];
            const i = copy.length - 1;
            if (copy[i] && copy[i].role === "assistant") {
              copy[i] = { ...copy[i], content: copy[i].content + delta };
            }
            return copy;
          });
        },
        onError: (err) => {
          setMessages((m) => {
            const copy = [...m];
            const i = copy.length - 1;
            copy[i] = { role: "assistant", content: "\u26a0\ufe0f Assistant error: " + err };
            return copy;
          });
        },
        onDone: () => {
          setMessages((m) => {
            const copy = [...m];
            const i = copy.length - 1;
            if (copy[i]?.role === "assistant") copy[i] = { ...copy[i], streaming: false };
            return copy;
          });
          setSending(false);
        },
      }
    );
    setSending(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen">
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur shrink-0">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> assistant
          {context && (<><ChevronRight className="h-3 w-3" /><span className="text-blue-400">{context}</span></>)}
        </span>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        <div className="max-w-3xl mx-auto">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="h-14 w-14 rounded-xl bg-blue-600/15 border border-blue-600/30 flex items-center justify-center mx-auto">
                <Sparkles className="h-7 w-7 text-blue-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mt-4">UC Engineer Pro Assistant</h2>
              <p className="text-zinc-400 mt-2 max-w-lg mx-auto">
                Ask anything about Cisco VoIP troubleshooting — cabling, VLANs, QoS, SIP/SCCP, CUCM, CUBE,
                gateways and RTP media. Answers include CLI, CUCM/RTMT paths and expected output.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => send(s)}
                    className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-300 hover:border-zinc-600 hover:-translate-y-px transition-[transform,border-color] duration-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-5">
            {messages.map((m, i) => (
              <div key={i} data-testid={`msg-${m.role}-${i}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-8 w-8 rounded-md shrink-0 flex items-center justify-center border ${m.role === "user" ? "bg-zinc-800 border-zinc-700" : "bg-blue-600/15 border-blue-600/30"}`}>
                  {m.role === "user" ? <User className="h-4 w-4 text-zinc-300" /> : <Bot className="h-4 w-4 text-blue-400" />}
                </div>
                <div className={`rounded-lg border p-4 text-sm leading-relaxed max-w-[85%] ${m.role === "user" ? "bg-zinc-800 border-zinc-700 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-200"}`}>
                  {m.role === "assistant" && m.content === "" && m.streaming ? (
                    <span className="text-zinc-500 font-mono">analyzing<span className="cursor-blink">_</span></span>
                  ) : (
                    <>
                      {renderContent(m.content)}
                      {m.streaming && <span className="cursor-blink text-blue-400">▍</span>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800 bg-zinc-950 px-4 sm:px-8 py-4 shrink-0">
        <div className="max-w-3xl mx-auto flex gap-3 items-end">
          <textarea
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Describe the VoIP issue… (Enter to send, Shift+Enter for newline)"
            className="flex-1 resize-none bg-zinc-900 border border-zinc-800 rounded-md px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-40"
          />
          <button
            onClick={() => send()}
            disabled={sending || !input.trim()}
            data-testid="assistant-send-btn"
            className="h-11 w-11 shrink-0 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
